/**
 * Supabase API Hooks
 *
 * React Query hooks for data fetching and mutations with Supabase.
 * These hooks handle caching, loading states, and error handling.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from './client';
import { rpc } from './rpc';
import type { Database } from './types';
import { reportError } from '@/lib/errors';
import { insertNotification } from '@/lib/notifications/client';
import { REQUEST_ACCESS_MESSAGE } from '@/lib/messaging/system-lines';
import { updateMyStartupProfile } from '@/lib/startup/my-startup-profile';

// Query Keys
export const queryKeys = {
  investorProfile: (userId: string) => ['investorProfile', userId] as const,
  score: (startupId: string) => ['score', startupId] as const,
  conversations: (userId: string) => ['conversations', userId] as const,
  /**
   * In-app notification feed. Keyed on the AUTH user id, because
   * notifications.user_id references profiles(id) — passing a
   * startup_profiles/investor_profiles id matches nothing.
   */
  notifications: (userId: string) => ['notifications', userId] as const,
  messages: (conversationId: string) => ['messages', conversationId] as const,
  dealflow: (investorId: string) => ['dealflow', investorId] as const,
  startups: () => ['startups'] as const,
  dataRoomDocuments: (startupId: string) => ['dataRoomDocuments', startupId] as const,
  accessRequests: (startupId: string) => ['accessRequests', startupId] as const,
  /** An investor's OWN access requests — the DD-entry signal (see lib/dd/dd-entry.ts). */
  myAccessRequests: (investorProfileId: string) =>
    ['myAccessRequests', investorProfileId] as const,
  /** Whether the founder's data room is open to the CALLING investor. */
  myDataRoomGrant: (startupId: string) => ['myDataRoomGrant', startupId] as const,
  submissionLinks: (investorId: string) => ['submissionLinks', investorId] as const,
  submissions: (investorId: string) => ['submissions', investorId] as const,
  submissionDetail: (submissionId: string) => ['submissionDetail', submissionId] as const,
  portfolio: (investorId: string) => ['portfolio', investorId] as const,
  commitments: (investorId: string) => ['commitments', investorId] as const,
  /** Investor track record — keyed on the AUTH user id, like `portfolio`. */
  trackRecord: (userId: string) => ['trackRecord', userId] as const,
  /** Derived engagement metrics. AUTH user id — the RPC bridges id spaces. */
  investorEngagement: (userId: string) => ['investorEngagement', userId] as const,
  /** Published founder reviews of an investor. AUTH user id. */
  investorReviews: (userId: string) => ['investorReviews', userId] as const,
  /** Investors who have engaged with the caller's startup(s). */
  relatedInvestors: () => ['relatedInvestors'] as const,
  /** One investor's full profile, read under the relationship gate. */
  investorPublicProfile: (profileId: string) =>
    ['investorPublicProfile', profileId] as const,
  /** Whether the caller's startup may review a given investor. */
  reviewEligibility: (investorUserId: string, startupId: string) =>
    ['reviewEligibility', investorUserId, startupId] as const,
};

// ============================================
// Profile Hooks
// ============================================

// NOTE: startup-profile read/upsert hooks keyed on `startup_profiles.user_id`
// were removed — that column is NULL for create-path founders, so they never
// worked for them and had no call sites. The caller's own startup is resolved
// via the membership seam (`src/lib/startup/use-my-startup.ts` +
// `my-startup-profile.ts`). Investor profiles legitimately own by `user_id`.

/**
 * Fetch investor profile for a user
 */
export function useInvestorProfile(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.investorProfile(userId || ''),
    queryFn: async () => {
      if (!userId || !isSupabaseConfigured()) return null;

      const { data, error } = await supabase
        .from('investor_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    },
    enabled: !!userId && isSupabaseConfigured(),
    staleTime: 10 * 60 * 1000, // 10 min — profile data changes infrequently
  });
}

/**
 * Persist the startup's data-room cloud connection to the database.
 *
 * Layer 1 of the data-room OAuth fix: a targeted update of just the connection
 * columns so the connection survives reload and is visible server-side
 * (dd-chat / investor access), instead of living only in the local store.
 * Resolves the caller's startup via the membership seam (NOT the legacy
 * `user_id`, which is NULL for create-path founders and matched zero rows,
 * silently dropping the connection). folderId NULL = provider root.
 */
export function useUpdateDataRoomConnection() {
  return useMutation({
    mutationFn: async (params: {
      connected: boolean;
      provider: 'google' | 'dropbox';
      folderId?: string | null;
    }) => {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }

      const update: Database['public']['Tables']['startup_profiles']['Update'] = {
        data_room_connected: params.connected,
        data_room_provider: params.provider,
        data_room_folder_id: params.folderId ?? null,
      };

      // Asserts rows-affected — a zero-row update returns error: null, so the
      // membership resolution is what makes this actually land.
      await updateMyStartupProfile(supabase, update);
    },
  });
}

/**
 * Create or update investor profile
 */
export function useUpsertInvestorProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      profile: Database['public']['Tables']['investor_profiles']['Insert']
    ) => {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }

      const { data, error } = await supabase
        .from('investor_profiles')
        .upsert(profile, { onConflict: 'user_id' })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.investorProfile(data.user_id),
      });
    },
  });
}

// ============================================
// Score Hooks
// ============================================

/**
 * Fetch score for a startup
 */
export function useScore(startupId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.score(startupId || ''),
    queryFn: async () => {
      if (!startupId || !isSupabaseConfigured()) return null;

      const { data, error } = await supabase
        .from('scores')
        .select('*')
        .eq('startup_id', startupId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    },
    enabled: !!startupId && isSupabaseConfigured(),
    staleTime: 5 * 60 * 1000, // 5 min — score data is moderately stable
  });
}

// ============================================
// Conversation & Messaging Hooks
// ============================================

/**
 * Fetch conversations for a user
 */
export function useConversations(
  userId: string | undefined,
  userType: 'startup' | 'investor'
) {
  return useQuery({
    queryKey: [...queryKeys.conversations(userId || ''), userType],
    queryFn: async () => {
      if (!userId || !isSupabaseConfigured()) return [];

      const column = userType === 'startup' ? 'startup_id' : 'investor_profile_id';

      // Embed the counterparty display names so the mapper can resolve
      // startupName / investorName without an extra round-trip. The embeds
      // resolve via the conversations_startup_id_fkey / _investor_id_fkey
      // foreign keys (both single-FK, so the table name is unambiguous).
      //
      // conversation_reads carries the CALLER's own unread count. It is
      // RLS-scoped to auth.uid(), so the embed yields at most their own row —
      // never a teammate's — and the list gets per-person unread without a
      // second request or an N+1.
      const { data, error } = await supabase
        .from('conversations')
        .select('*, startup_profiles(name), investor_profiles(name, firm), conversation_reads(unread_count)')
        .eq(column, userId)
        .order('last_message_time', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId && isSupabaseConfigured(),
    staleTime: 60 * 1000, // 1 min — conversation list updates moderately often
  });
}

/**
 * Fetch messages for a conversation
 */
export function useMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.messages(conversationId || ''),
    queryFn: async () => {
      if (!conversationId || !isSupabaseConfigured()) return [];

      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!conversationId && isSupabaseConfigured(),
    staleTime: 30 * 1000, // 30 sec — messages need near-real-time freshness
  });
}

/**
 * Notify whichever participant did NOT send the message.
 *
 * The recipient's auth id is not on the conversation row: it holds
 * `startup_id` and `investor_profile_id`, so the sender's `sender_type` decides
 * which side to resolve and through which table.
 *
 * `startup_profiles.user_id` is read here for ADDRESSING ONLY, never to decide
 * authorisation — the ownership invariant forbids the latter, and the
 * data-room-request notification above already resolves the founder the same
 * way. An investor's auth id comes from `investor_profiles.user_id`.
 */
async function notifyMessageRecipient(
  message: Database['public']['Tables']['messages']['Insert'],
): Promise<void> {
  const { data: conversation, error } = await supabase
    .from('conversations')
    .select('startup_id, investor_profile_id')
    .eq('id', message.conversation_id)
    .maybeSingle();
  if (error || !conversation) return;

  const senderIsStartup = message.sender_type === 'startup';
  const investorProfileId = conversation.investor_profile_id;
  // The investor erased their account (FK SET NULL): nobody to notify. RLS
  // already refuses new messages into such a thread; this keeps the lookup
  // from ever running with a NULL id.
  if (senderIsStartup && investorProfileId === null) return;

  const { data: recipient } = senderIsStartup && investorProfileId !== null
    ? await supabase
        .from('investor_profiles')
        .select('user_id')
        .eq('id', investorProfileId)
        .maybeSingle()
    : await supabase
        .from('startup_profiles')
        .select('user_id')
        .eq('id', conversation.startup_id)
        .maybeSingle();

  if (!recipient?.user_id) return;

  insertNotification({
    userId: recipient.user_id,
    type: 'message',
    title: 'New message',
    // The body is deliberately not the message text. Notifications are
    // addressed by user id and this table's INSERT policy is `WITH CHECK
    // (true)`, so rows are broadly readable-by-construction in a way the
    // message itself should not be.
    body: 'You have a new message.',
    data: { conversationId: message.conversation_id },
  });
}

/**
 * Send a message
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (message: Database['public']['Tables']['messages']['Insert']) => {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }

      const { data, error } = await supabase
        .from('messages')
        .insert(message)
        .select()
        .single();

      if (error) throw error;

      // conversations.last_message / last_message_time / the unread counters
      // are now SERVER-derived (public.touch_conversation_on_message(), an
      // AFTER INSERT trigger on messages — see
      // 20260916120000_ugc_block_report_filter.sql). The client used to write
      // these columns itself right here, which meant the write was gated by
      // nothing but the permissive participant UPDATE policy — a blocked
      // party could still post an unfiltered inbox preview by calling
      // `.update()` directly, since blocking only closed the INSERT doors.
      // Do not reintroduce a client write here: `guard_conversation_client_update()`
      // (BEFORE UPDATE trigger) now rejects any client-originated change to
      // these columns with 42501, so writing them again would just fail.

      // Tell the OTHER participant. Until now sending a message notified nobody
      // at all — not specific to any one flow, every message in the app was
      // silent — so a founder only learned of an investor's message by opening
      // the app and looking. Fire-and-forget: a notification that fails must
      // not fail the message.
      try {
        await notifyMessageRecipient(message);
      } catch (notifyError) {
        reportError(notifyError, {
          context: 'useSendMessage.notifyRecipient',
          level: 'warning',
          extra: { conversationId: message.conversation_id },
        });
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.messages(data.conversation_id),
      });
      queryClient.invalidateQueries({
        queryKey: ['conversations'],
      });
    },
  });
}

/**
 * Create (or return the existing) conversation between a startup and investor.
 *
 * Creation is RLS relationship-gated (see migration
 * 20260717170000_messaging_realtime_and_gating.sql): a thread may only be opened
 * once the investor is in the startup's dealflow OR holds a GRANTED
 * data_room_access. When the relationship gate denies the insert, Postgres
 * raises an RLS error (code 42501 / PGRST301) — this hook surfaces that error to
 * the caller (does NOT swallow it) so the UI can show an honest message.
 *
 * Idempotent: if a conversation already exists for the (startup, investor) pair
 * it is returned as-is rather than re-inserted.
 */
/**
 * Find the founder↔investor conversation, creating it if there isn't one.
 *
 * A plain function rather than a hook so it can be reused inside another
 * mutation — requesting data-room access posts a message and therefore needs a
 * conversation, and hooks cannot be called from a mutationFn.
 *
 * Select-existing-then-insert (rather than upsert) so that the RLS relationship
 * gate — which only guards INSERT — is not tripped when a valid conversation
 * already exists.
 *
 * Throws on a gate denial rather than swallowing it; callers decide whether
 * that is fatal.
 */
async function findOrCreateConversation(startupId: string, investorId: string) {
  const { data: existing, error: selectError } = await supabase
    .from('conversations')
    .select('*, startup_profiles(name), investor_profiles(name, firm)')
    .eq('startup_id', startupId)
    .eq('investor_profile_id', investorId)
    .maybeSingle();

  if (selectError) throw selectError;
  if (existing) return existing;

  const { data, error } = await supabase
    .from('conversations')
    .insert({ startup_id: startupId, investor_profile_id: investorId })
    .select('*, startup_profiles(name), investor_profiles(name, firm)')
    .single();

  // Deliberately re-thrown, not swallowed: the RLS relationship-gate denial
  // arrives here and must reach the caller's catch block.
  if (error) throw error;
  return data;
}

export function useCreateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      startupId,
      investorId,
    }: {
      startupId: string;
      investorId: string;
    }) => {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }
      return findOrCreateConversation(startupId, investorId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

// ============================================
// Dealflow Hooks
// ============================================

/**
 * Fetch dealflow for an investor
 */
export function useDealflow(investorId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.dealflow(investorId || ''),
    queryFn: async () => {
      if (!investorId || !isSupabaseConfigured()) return [];

      const { data, error } = await supabase
        .from('dealflow')
        .select(`
          *,
          startup:startup_profiles(*)
        `)
        .eq('investor_profile_id', investorId)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!investorId && isSupabaseConfigured(),
    staleTime: 2 * 60 * 1000, // 2 min — dealflow list (uses default)
  });
}

/**
 * Update dealflow status
 */
export function useUpdateDealflowStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      dealflowId,
      status,
      notes,
    }: {
      dealflowId: string;
      status: Database['public']['Tables']['dealflow']['Row']['status'];
      notes?: string;
    }) => {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }

      const { data, error } = await supabase
        .from('dealflow')
        .update({ status, notes, updated_at: new Date().toISOString() })
        .eq('id', dealflowId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dealflow(data.investor_profile_id),
      });
    },
  });
}

// NOTE: the former `useRequestDataRoomAccess` hook was DELETED (2026-07-30).
// It had zero callers — both real call sites (startup-detail.tsx,
// (investor)/dealflow.tsx) use `useRequestDataRoomAccessNew`, which writes a
// `data_room_access_requests` row for the founder to answer.
//
// The old one was a mock-era leftover ("In a real implementation, this would
// create an access request — for now, we just update the dealflow record") that
// had the INVESTOR run `update dealflow set data_room_access = true` on their
// own row. Investors DO hold an UPDATE policy on dealflow, so unlike the
// founder's version of that write it would have succeeded: an investor could
// self-grant the DD screen's "access granted" state with no founder involvement.
// Worse than it looked at the time — a SELECT policy on data_room_documents read
// that same column, so it really did open the founder's documents (measured on
// staging: 0 → 2, with zero grant rows). Both the column and that policy are
// gone as of 20260805160000, and a trigger now refuses the stage that replaced
// them to any client role. Deleted rather than fixed: `…AccessNew` is the real
// path.

// NOTE: the former useDiscoverStartups hook (startup_profiles joined to the
// DEPRECATED `scores` table, zero consumers) was deleted. The raising-startups
// feed is `useRaisingStartups()` in investor-queries.ts, backed by
// `score_event` — the post-cutover source of truth.

// ============================================
// Score Persistence Hooks
// ============================================

/**
 * Create or update a score for a startup
 */
export function useUpsertScore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      score: Database['public']['Tables']['scores']['Insert']
    ) => {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }

      const { data, error } = await supabase
        .from('scores')
        .upsert(score, { onConflict: 'startup_id' })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.score(data.startup_id),
      });
    },
  });
}

// ============================================
// Data Room Hooks
// ============================================

/**
 * Fetch data room documents for a startup
 */
export function useDataRoomDocuments(startupId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.dataRoomDocuments(startupId || ''),
    queryFn: async () => {
      if (!startupId || !isSupabaseConfigured()) return [];

      const { data, error } = await supabase
        .from('data_room_documents')
        .select('*')
        .eq('startup_id', startupId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!startupId && isSupabaseConfigured(),
  });
}

/**
 * Has this startup's founder granted the CALLING investor data-room access?
 *
 * The producer for `ConversationEligibility.dataRoomAccessGranted`. Before this
 * existed, that flag was fed `dataRoomOpen(deal.status)` — a parameter computed
 * from the other parameter, so it could never change an outcome once
 * `diligence` opened the channel on its own. The grant is a genuinely separate
 * fact, and the stage cannot stand in for it in either direction.
 *
 * Since 20260806090000, concluding as `pass` or `monitor` DOES revoke the grant
 * (`invest` deliberately does not). What survives is a grant a founder made
 * outside a diligence, or one that outlived a conclusion the RPC never saw —
 * so a `rejected` deal can still carry a live grant, and the conversations
 * INSERT policy honours it. `useConcludeDD` invalidates this key, because the
 * RPC now changes the answer.
 *
 * MIRRORS THE POLICY'S PREDICATE EXACTLY: `status = 'granted'`, nothing more.
 * Deliberately NOT also `revoked_at IS NULL` / expiry. Every DATA-ROOM gate
 * asks `has_data_room_access` (granted, not revoked, not expired); the thing this
 * boolean shadows is the conversations INSERT policy (Door A clause 2), which
 * checks status alone. A stricter client would hide a button the server would
 * honour. Do not "tidy" this by adding filters — it is the one reviewed
 * exception in the grant-predicate guard
 * (supabase/functions/_shared/authz/data-room-pass_test.ts).
 *
 * INVESTOR-SIDE ONLY. No `investor_user_id` filter is needed: the `Investors can
 * view own access` policy (`investor_user_id = auth.uid()`) already scopes an
 * investor to their own row. A FOUNDER calling this would match the owner policy
 * (`is_startup_owner_or_member`) instead and see EVERY grant on their startup,
 * making it answer a different question. Its one caller is investor-side.
 *
 * `.maybeSingle()` is safe BECAUSE of that scoping plus the live unique index
 * `unique_data_room_access (startup_id, investor_user_id)`: an investor can see
 * at most one row here. A founder would see many and this would 406.
 */
export function useMyDataRoomGrant(startupId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.myDataRoomGrant(startupId || ''),
    queryFn: async (): Promise<boolean> => {
      if (!startupId || !isSupabaseConfigured()) return false;

      const { data, error } = await supabase
        .from('data_room_access')
        .select('status')
        .eq('startup_id', startupId)
        .eq('status', 'granted')
        .maybeSingle();

      if (error) throw error;
      return !!data;
    },
    enabled: !!startupId && isSupabaseConfigured(),
  });
}

/**
 * Add a document to the data room
 */
export function useAddDataRoomDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      document: Database['public']['Tables']['data_room_documents']['Insert']
    ) => {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }

      const { data, error } = await supabase
        .from('data_room_documents')
        .insert(document)
        .select()
        .single();

      if (error) throw error;

      // Notify the investors who hold the data-room pass. The notification
      // carries the document's NAME, and data-room file names are proof
      // (SCORE VISIBILITY INVARIANT), so the recipients are decided by
      // has_data_room_access — the one definition (granted, not revoked, not
      // expired) — per investor, never by a status filter here: that copy
      // ignored revoked_at and expires_at and told investors whose grant had
      // expired what the founder uploaded. A failed check notifies nobody.
      const { data: accessRows, error: accessError } = await supabase
        .from('data_room_access')
        .select('investor_user_id')
        .eq('startup_id', data.startup_id);
      if (accessError) {
        reportError(accessError, { context: 'useAddDataRoomDocument.notifyRecipients', level: 'warning' });
      }
      const candidates = [...new Set((accessRows ?? []).map((r) => r.investor_user_id))];
      const holders = (
        await Promise.all(
          candidates.map(async (investorId) => {
            const { data: holds, error: passError } = await rpc('has_data_room_access', {
              p_startup_id: data.startup_id,
              p_investor_id: investorId,
            });
            if (passError) {
              reportError(passError, { context: 'useAddDataRoomDocument.passCheck', level: 'warning' });
              return null;
            }
            return holds === true ? investorId : null;
          }),
        )
      ).filter((id): id is string => id !== null);

      if (holders.length > 0) {
        holders.forEach((investorUserId) => {
          insertNotification({
            userId: investorUserId,
            type: 'system',
            title: 'New document in data room',
            body: `A new document "${data.name ?? 'file'}" has been added to the data room.`,
            data: { documentId: data.id, startupId: data.startup_id },
          });
        });
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dataRoomDocuments(data.startup_id),
      });
    },
  });
}

/**
 * Delete a document from the data room.
 *
 * Phase 115 / Plan 03 rewire: invokes the `delete-data-room-document` edge
 * function so SEC-DOC-15's "delete data AND storage object AND log event"
 * guarantee runs server-side under the service-role client (which the helper
 * `_shared/lifecycle/document.ts:deleteDocument` enforces). The previous
 * client-side bare `.delete()` could only touch the row — storage + tombstone
 * + TEXT-keyed child cleanup would silently drift, and any caller could
 * bypass the protection.
 *
 * Mutation signature is unchanged: `{ documentId, startupId }`. The
 * `startupId` is used only for cache invalidation; the edge function
 * resolves it from `data_room_documents.startup_id` server-side.
 */
export function useDeleteDataRoomDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, startupId }: { documentId: string; startupId: string }) => {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }

      const { data, error } = await supabase.functions.invoke('delete-data-room-document', {
        body: { documentId },
      });

      if (error) throw error;
      return { documentId, startupId, result: data };
    },
    onSuccess: ({ startupId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dataRoomDocuments(startupId),
      });
    },
  });
}

/**
 * Fetch access requests for a startup
 */
export function useDataRoomAccessRequests(startupId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.accessRequests(startupId || ''),
    queryFn: async () => {
      if (!startupId || !isSupabaseConfigured()) return [];

      const { data, error } = await supabase
        .from('data_room_access_requests')
        .select(`
          *,
          investor:investor_profiles(id, name, firm, linkedin_verified)
        `)
        .eq('startup_id', startupId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!startupId && isSupabaseConfigured(),
  });
}

/**
 * Fetch the CURRENT INVESTOR's own data-room access requests.
 *
 * This is the DD-entry signal: a request row means the investor explicitly
 * started due diligence on that startup (see `lib/dd/dd-entry.ts`). The DD
 * screen and the investor dashboard both derive their lists from it, so they
 * cannot drift the way the old `dataRoomAccess`-vs-`status` predicates did.
 *
 * `investorProfileId` is an `investor_profiles.id` — NOT the auth user id.
 * That matches `data_room_access_requests.investor_profile_id` and the RLS policy
 * "Investors can view their requests".
 */
export function useMyDataRoomAccessRequests(investorProfileId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.myAccessRequests(investorProfileId || ''),
    queryFn: async () => {
      if (!investorProfileId || !isSupabaseConfigured()) return [];

      const { data, error } = await supabase
        .from('data_room_access_requests')
        .select('startup_id, investor_profile_id, status, created_at, responded_at')
        .eq('investor_profile_id', investorProfileId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
    enabled: !!investorProfileId && isSupabaseConfigured(),
  });
}

/**
 * Request access to a startup's data room (investor action)
 */
export function useRequestDataRoomAccessNew() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      startupId,
      investorId,
      investorUserId,
      message,
    }: {
      startupId: string;
      /** `investor_profiles.id` — what the request row is keyed on. */
      investorId: string;
      /**
       * `auth.users.id`. Needed only to author the chat message, because the
       * messages RLS INSERT policy checks `sender_id = auth.uid()`. Optional:
       * without it the request still succeeds, it just goes unannounced.
       */
      investorUserId?: string;
      message?: string;
    }) => {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }

      // INSERT, NOT UPSERT.
      //
      // The table is UNIQUE (startup_id, investor_profile_id), so an upsert took
      // the ON CONFLICT DO UPDATE branch on any second request — and the
      // investor has no UPDATE policy here; only the startup side does. Every
      // re-request was refused with "new row violates row-level security policy
      // (USING expression)" (INVERIO-APP-P/-Q/-X/-Y, both platforms).
      //
      // Granting that policy would be the wrong fix: RLS cannot restrict
      // columns, so it would also let an investor write their own `status`.
      // Under the product rule — pending is unreachable because the button
      // refuses it, and a rejection is final — an investor never legitimately
      // updates a request. So the write is insert-only and the missing policy
      // is the correct model, with the database as the backstop if the UI ever
      // regresses.
      const { data, error } = await supabase
        .from('data_room_access_requests')
        .insert({
          startup_id: startupId,
          investor_profile_id: investorId,
          message,
          status: 'pending' as const,
        })
        .select()
        .single();

      // 23505 = the row already exists, i.e. they have already asked. That is
      // not an error to show anyone: the button should have been reflecting the
      // existing state, and this is the race where it had not caught up yet.
      // Re-read so the caller invalidates on real values and the UI converges.
      if (error?.code === '23505') {
        const { data: existing, error: readError } = await supabase
          .from('data_room_access_requests')
          .select()
          .eq('startup_id', startupId)
          .eq('investor_profile_id', investorId)
          .single();
        if (readError) throw readError;
        return { request: existing, created: false };
      }
      if (error) throw error;

      // Notify startup owner about access request
      const { data: sp } = await supabase
        .from('startup_profiles')
        .select('user_id')
        .eq('id', startupId)
        .maybeSingle();
      if (sp?.user_id) {
        insertNotification({
          userId: sp.user_id,
          type: 'data_room_request',
          title: 'Data room access requested',
          body: 'An investor has requested access to your data room.',
          data: { requestId: data.id, investorId },
        });
      }

      // Open the channel and say so in it, so the ask lands somewhere the
      // founder can answer rather than only in a notification.
      //
      // SECONDARY AND NON-BLOCKING. The request row is the fact that matters
      // and it is already written; failing the whole action because a message
      // could not be posted would strand the investor with no way to retry
      // (the row now exists, so the retry is refused). It genuinely can fail:
      // a raising-feed request has no dealflow row, and the conversations
      // INSERT policy is a relationship gate that reads one.
      if (investorUserId) {
        try {
          const conversation = await findOrCreateConversation(startupId, investorId);
          const { error: messageInsertError } = await supabase.from('messages').insert({
            conversation_id: conversation.id,
            // MUST be the auth uid — the messages RLS INSERT policy checks
            // sender_id = auth.uid().
            sender_id: investorUserId,
            sender_type: 'investor',
            text: REQUEST_ACCESS_MESSAGE,
              });
          // supabase-js reports failure via `{ error }`, not by throwing, so an
          // unchecked result here would be a silent no-op — the founder would
          // simply never see the message and nothing would say why (ARCH-06).
          if (messageInsertError) throw messageInsertError;
        } catch (messageError) {
          reportError(messageError, {
            context: 'useRequestDataRoomAccessNew.postRequestMessage',
            level: 'warning',
            extra: { startupId },
          });
        }
      }

      return { request: data, created: true };
    },
    onSuccess: ({ request }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accessRequests(request.startup_id),
      });
      // The request row IS the DD-entry signal, so the investor's DD screen and
      // dashboard must refetch — otherwise "Start due diligence" appears to do
      // nothing until the next cold load.
      queryClient.invalidateQueries({
        queryKey: queryKeys.myAccessRequests(request.investor_profile_id),
      });
      // The request and the grant are two facts about one relationship; they
      // must never be refetched apart, or the messaging gate reads a stale
      // grant against a fresh request.
      queryClient.invalidateQueries({
        queryKey: queryKeys.myDataRoomGrant(request.startup_id),
      });
      // A message may have been posted into the thread.
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

/**
 * Respond to a data room access request (startup action)
 */
export function useRespondToAccessRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      requestId,
      startupId,
      status,
    }: {
      requestId: string;
      startupId: string;
      status: 'approved' | 'rejected';
    }) => {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured');
      }

      // Single atomic RPC. This used to be two client-side writes: an UPDATE on
      // the request row, then an UPDATE on `dealflow` to flip
      // `data_room_access`. The second one is RLS-IMPOSSIBLE for a founder —
      // there is no founder UPDATE policy on `dealflow`, so it was filtered to
      // zero rows and returned NO error, meaning the `if (dealflowError)` guard
      // never fired and access was never actually granted. See
      // supabase/migrations/20260730120000_data_room_response_rpc.sql.
      const { data, error } = await rpc('respond_to_data_room_request', {
        p_request_id: requestId,
        p_approve: status === 'approved',
      }).single();

      if (error) throw error;

      const result = data as { startup_id: string; investor_profile_id: string; status: string };

      // Notify the investor. `result.investor_profile_id` is an investor_profiles.id,
      // but notifications key off the AUTH user id — passing the profile id
      // straight through (as this did before) addressed the notification to a
      // user that does not exist, so the investor was never told. Same id
      // confusion that produced the RLS bug above.
      const { data: investorRow } = await supabase
        .from('investor_profiles')
        .select('user_id')
        .eq('id', result.investor_profile_id)
        .maybeSingle();

      if (investorRow?.user_id) {
        const approved = status === 'approved';
        insertNotification({
          userId: investorRow.user_id,
          type: approved ? 'data_room_approved' : 'system',
          title: approved ? 'Data room access granted' : 'Data room access declined',
          body: approved
            ? 'Your data room access request has been approved.'
            : 'Your data room access request was declined.',
          data: { requestId, startupId, status },
        });
      } else {
        reportError('investor_profiles row has no user_id — decision not notified', {
          context: 'useRespondToAccessRequest.notifyInvestor',
          level: 'warning',
          extra: { requestId, investorProfileId: result.investor_profile_id },
        });
      }

      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accessRequests(data.startup_id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dealflow(data.investor_profile_id),
      });
      // The investor's DD list reads the request rows, so it must refetch too
      // or the founder's decision stays invisible until a cold load.
      queryClient.invalidateQueries({
        queryKey: queryKeys.myAccessRequests(data.investor_profile_id),
      });
      // `respond_to_data_room_request` writes the request row AND the
      // `data_room_access` grant in ONE transaction, so the two cached facts
      // must be invalidated together or they drift.
      queryClient.invalidateQueries({
        queryKey: queryKeys.myDataRoomGrant(data.startup_id),
      });
    },
  });
}

// ============================================
// Utility Hooks
// ============================================

/**
 * Check if Supabase is configured and ready
 */
export function useSupabaseStatus() {
  return {
    isConfigured: isSupabaseConfigured(),
  };
}
